import React from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import Popover from 'components/lib/Popover';
import Tooltip from 'components/lib/Tooltip';
import { useSalesBattlecardContext } from 'components/SalesBattlecard/SalesBattlecardContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { generateWidgetHeaderTitleDataTestId } from 'helpers/tests';
import { localizeAreaLabel } from 'utils/helpers/units';

import { useWidgetContext } from './WidgetContext';
import WidgetFiltersInfo from './WidgetFiltersInfo';
import { WIDGET_TYPES } from '../constants';
import GraphWidgetDisplayControls from '../GraphWidget/GraphWidgetDisplayControls';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  &.map {
    position: absolute;
    right: 20px;
    top: 30px;
    z-index: 5;
  }
  &.amenities {
    position: absolute;
    right: 15px;
    top: 10px;
    z-index: 5;
    width: 15px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
`;

const Title = styled.span`
  font-family: var(--font-bold);
  font-size: 18px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const Note = styled.div`
  font-size: 12px;
  color: var(--gray-dark);
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  line-clamp: 2;
  white-space: normal;
`;

const ContainerPopover = styled.div`
  display: flex;
`;

const WidgetHeader = () => {
  const { name, note, type } = useWidgetContext();
  const { isViewSalesBattlecard, competitors, community } = useCommunityViewContext();
  const { selectedCompetitorId } = useSalesBattlecardContext();

  const displayName = (() => {
    if (isViewSalesBattlecard && type === WIDGET_TYPES.AI_INSIGHTS) {
      if (selectedCompetitorId === null) return 'Market AI Insights';
      const competitor = competitors.find((c: any) => c?.id === selectedCompetitorId);
      return localizeAreaLabel(competitor ? `${competitor.comunity_name} AI Insights` : name, community);
    }
    return localizeAreaLabel(name, community);
  })();

  const showTitle = ![WIDGET_TYPES.MAP, WIDGET_TYPES.AMENITIES].includes(type);
  const showNote = ![WIDGET_TYPES.AMENITIES].includes(type);

  return (
    <Container
      className={classNames({
        map: type === WIDGET_TYPES.MAP,
        amenities: type === WIDGET_TYPES.AMENITIES,
      })}
    >
      <Row>
        {showTitle && (
          <Tooltip title={<div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{displayName}</div>}>
            <Title title={displayName} data-testid={generateWidgetHeaderTitleDataTestId(type, displayName)}>
              {displayName}
            </Title>
          </Tooltip>
        )}
        <GraphWidgetDisplayControls />
      </Row>
      <Tooltip title={<WidgetFiltersInfo />}>
        <ContainerPopover>
          <WidgetFiltersInfo />
        </ContainerPopover>
      </Tooltip>
      {showNote && (
        <Tooltip title={<div style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{note}</div>}>
          <Note>{note}</Note>
        </Tooltip>
      )}
    </Container>
  );
};

export default WidgetHeader;
